/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App} from '../App'
import {getDashboardPath} from '../utils/helper'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const basename = process.env.PUBLIC_URL || '/'

const AppRoutes: FC = () => {
  const {currentUser, hasPermission} = useAuth()

  const getRedirectPath = () => {
    // If they have dashboard access, take them to the default dashboard
    if (hasPermission('admin.dashboard.stats')) {
      const role = currentUser?.role || currentUser?.user_type
      const userType = currentUser?.user_type
      return getDashboardPath(role, userType)
    }

    // Otherwise find the first available page based on common permissions
    if (hasPermission('admin.blog.list')) return '/blog-access'
    if (hasPermission('admin.users.list')) return '/payairo-users'
    if (hasPermission('admin.transactions.list')) return '/transactions/fiat'
    if (hasPermission('admin.staff.list')) return '/access-management'
    if (hasPermission('admin.roles.read')) return '/role-management'
    
    // Fallback to their own profile page if nothing else is permitted
    return '/settings'
  }

  return (
    <BrowserRouter basename={basename}>
      <Routes>
        <Route element={<App />}>
          <Route path='logout' element={<Logout />} />
          <Route path='error/*' element={<ErrorsPage />} />
          {currentUser ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to={getRedirectPath()} />} />
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
